import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SiPaypal, SiStripe, SiKofi } from "react-icons/si";
import { TiltCard } from "@/components/TiltCard";
import { BrandIcon } from "@/components/BrandIcon";
import { HandGuide } from "@/components/HandGuide";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const MotionLink = motion.create(Link);

// ---------------------------------------------------------------------------
// TODO (owner): replace these placeholder URLs with your real links.
// ---------------------------------------------------------------------------
const DONATION_METHODS = [
  { key: "paypal", label: "PayPal", Icon: SiPaypal, url: "https://www.paypal.com/ncp/payment/TNXZD87WEFUKC" },
  { key: "stripe", label: "Stripe", Icon: SiStripe, url: "https://buy.stripe.com/00wcN4fHb1qc3f288U8IU00" },
  { key: "kofi", label: "Ko-fi", Icon: SiKofi, url: "https://ko-fi.com/doriantelive/tip" },
];
// Payment logos appended to the END of the logo line (link to the same donation URLs)
const PAYMENT_LOGOS = DONATION_METHODS.map((m) => ({ key: m.key, label: m.label, url: m.url }));
const DISCORD_ROUTE = "/discord-soon";
const CHARITY_URL = "example whatever whatever"; // TODO (owner): real charity link

const bigButtonClass =
  "inline-flex items-center rounded-full border border-matrix-green/60 px-9 py-4 font-display text-2xl font-black uppercase tracking-tighter text-white no-underline outline-none transition-all duration-300 hover:bg-matrix-green/10 hover:shadow-[0_0_30px_rgba(0,255,136,0.35)] sm:text-4xl";

export const Socials = ({ socials }) => {
  const list = socials || [];
  const boxes = list.filter((s) => s.display === "box");
  const logoRow = [...list, ...PAYMENT_LOGOS]; // socials first, payment logos last
  const [donateOpen, setDonateOpen] = useState(false);

  return (
    <section
      id="socials"
      data-testid="socials-section"
      className="relative z-20 mx-auto max-w-6xl px-6 py-24 sm:px-10 lg:px-16 lg:py-32"
    >
      <div className="mb-14 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-matrix-green/50" />
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-matrix-green">
            socials_&_projects.grid
          </span>
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-4xl font-black uppercase tracking-tighter text-white sm:text-6xl"
        >
          Enter the <span className="text-stroke">Grid</span>
        </motion.h2>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="social-grid">
        {boxes.map((item, i) => (
          <TiltCard key={item.key} item={item} index={i} />
        ))}
      </div>

      {/* Logo quick links, full-width & evenly spaced (first/last symmetric to edges) */}
      <div className="mt-[15px] flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-3" data-testid="social-logos-row">
        {logoRow.map((item, i) => {
          const Wrapper = item.internal ? MotionLink : motion.a;
          const linkProps = item.internal
            ? { to: item.url }
            : { href: item.url, target: "_blank", rel: "noopener noreferrer" };
          return (
            <Wrapper
              key={item.key}
              {...linkProps}
              data-testid={`social-logo-${item.key}`}
              aria-label={item.label}
              title={item.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              whileHover={{ y: -4 }}
              className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-matrix-green/25 bg-matrix-ink/50 text-white/70 no-underline transition-all duration-300 hover:border-matrix-green/70 hover:text-matrix-green hover:shadow-[0_0_22px_rgba(0,255,136,0.3)] sm:h-12 sm:w-12"
            >
              <BrandIcon name={item.key} className="text-lg" />
            </Wrapper>
          );
        })}
      </div>

      {/* Action buttons: Support + Discord (centered pair), Charity 10px below */}
      <div className="mt-[25px] flex flex-col items-center gap-[10px]">
        <div className="flex flex-wrap items-center justify-center gap-5">
          <motion.button
            type="button"
            data-testid="support-channel-btn"
            onClick={() => setDonateOpen(true)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={bigButtonClass}
          >
            Support the Channel
          </motion.button>
          <MotionLink
            to={DISCORD_ROUTE}
            data-testid="join-discord-btn"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={bigButtonClass}
          >
            Join the Discord
          </MotionLink>
        </div>
        <motion.a
          href={CHARITY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="support-charity-btn"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={bigButtonClass}
        >
          Support the Charity
        </motion.a>
      </div>

      {/* Donation method dialog */}
      <Dialog open={donateOpen} onOpenChange={setDonateOpen}>
        <DialogContent
          data-testid="donate-dialog"
          className="border-matrix-green/40 bg-matrix-ink text-white sm:max-w-sm"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-matrix-green">
              Support the Channel
            </DialogTitle>
            <DialogDescription className="font-mono text-xs text-white/50">
              Choose a donation method. Every bit fuels the climb.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 pt-2">
            {DONATION_METHODS.map((m) => (
              <a
                key={m.key}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`donate-${m.key}`}
                className="flex flex-col items-center gap-3 rounded-lg border border-matrix-green/25 bg-black/40 p-5 text-white/70 no-underline transition-all duration-300 hover:border-matrix-green/70 hover:text-matrix-green hover:shadow-[0_0_22px_rgba(0,255,136,0.25)]"
              >
                <m.Icon className="text-3xl" />
                <span className="font-mono text-[11px] uppercase tracking-wider">{m.label}</span>
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Guiding hand — locked to the grid/logo element positions (scrolls with section) */}
      <HandGuide />
    </section>
  );
};
